import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Bot, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

const STORAGE_KEY = "ai_chat_consent";
const CONSENT_VERSION = "2026.02";

function getStored() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch { return null; }
}

export function hasAiConsent() {
  const c = getStored();
  return !!c && c.version === CONSENT_VERSION && c.accepted;
}

/**
 * Modal that gates access to any AI chat surface. If the user has already
 * accepted THIS document version, renders children directly.
 * Otherwise shows a blocking modal with the AI disclaimer.
 */
export default function ChatConsentGate({ children, onCancelHref = "/" }) {
  const [accepted, setAccepted] = useState(() => hasAiConsent());
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    // Re-check on mount in case another tab updated it
    setAccepted(hasAiConsent());
  }, []);

  const accept = () => {
    const payload = { accepted: true, version: CONSENT_VERSION, at: new Date().toISOString() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    // Ledger call — best effort
    api.post("/consents/record", {
      kind: "ai",
      accepted: true,
      version: CONSENT_VERSION,
      details: { surface: "chat" },
    }).catch(() => { /* silent */ });
    setAccepted(true);
  };

  if (accepted) return children;

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ai-consent-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border border-border">
        <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white p-5 flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Bot className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-display text-xl leading-tight">Antes de continuar</div>
            <div className="text-sm opacity-90 mt-1">
              Vas a interactuar con un asistente de <b>Inteligencia Artificial</b>.
              Reglamento (UE) 2024/1689 · AI Act Art. 52.
            </div>
          </div>
          <Link to={onCancelHref} className="text-white/80 hover:text-white p-1" aria-label="Cerrar" data-testid="ai-consent-close">
            <X className="w-5 h-5" />
          </Link>
        </div>

        <div className="p-5 sm:p-6 space-y-4 text-sm max-h-[60vh] overflow-y-auto">
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-emerald-900">Sí puede hacer</div>
              <div className="text-emerald-900/90">
                Orientarte sobre síntomas comunes, explicarte conceptos médicos generales y
                sugerirte cuándo consultar al médico.
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-rose-50 border border-rose-200 rounded-lg p-3">
            <AlertTriangle className="w-5 h-5 text-rose-700 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-rose-900">NO puede hacer</div>
              <div className="text-rose-900/90">
                Diagnosticar, prescribir, ni sustituir a un médico. Para urgencias vitales llama al <b>112</b>.
                Toda decisión clínica corresponde al <b>Dr. Dimy Laguerre</b> (supervisión humana obligatoria).
              </div>
            </div>
          </div>

          <div className="text-muted-foreground text-xs leading-relaxed">
            <b>Modelo</b>: Claude Sonnet 4.5 · <b>Región</b>: UE · <b>Retención en el proveedor</b>:
            30 días para auditoría · Los datos NO se usan para entrenar el modelo.{" "}
            Consulta la <Link to="/uso-ia" className="text-primary underline">Política de uso de IA</Link> completa
            y la <Link to="/privacidad" className="text-primary underline">Política de Privacidad</Link>.
          </div>

          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border hover:bg-muted/30">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5"
              data-testid="ai-consent-checkbox"
            />
            <span className="text-sm">
              He leído las limitaciones, entiendo que <b>la IA no sustituye a un médico</b> y
              acepto que mi consentimiento quede registrado electrónicamente (versión {CONSENT_VERSION}).
            </span>
          </label>
        </div>

        <div className="p-4 border-t bg-muted/20 flex flex-wrap gap-2 justify-end">
          <Link to={onCancelHref}>
            <Button variant="ghost" data-testid="ai-consent-cancel">Cancelar</Button>
          </Link>
          <Button onClick={accept} disabled={!checked} className="bg-primary hover:bg-primary/90" data-testid="ai-consent-accept">
            Acepto y continúo
          </Button>
        </div>
      </div>
    </div>
  );
}
